/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  ReactNode,
} from 'react';
import {
  Integration,
  SyncLog,
  CreateIntegrationInput,
  UpdateIntegrationInput,
  SyncOptions,
  IntegrationHealth,
} from './types';
import { supabase } from '../../services/supabaseClient';
import { resolveFunctionsHttpError } from '../../services/edgeFunctionErrors';

/**
 * Integration Context State
 */
interface IntegrationState {
  integrations: Integration[];
  syncLogs: SyncLog[];
  activeIntegration: Integration | null;
  health: IntegrationHealth | null;
  isLoading: boolean;
  error: string | null;
  isSyncing: boolean;
}

/**
 * Integration Context Actions
 */
type IntegrationAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_SYNCING'; payload: boolean }
  | { type: 'SET_INTEGRATIONS'; payload: Integration[] }
  | { type: 'ADD_INTEGRATION'; payload: Integration }
  | {
      type: 'UPDATE_INTEGRATION';
      payload: { id: string; updates: Partial<Integration> };
    }
  | { type: 'DELETE_INTEGRATION'; payload: string }
  | { type: 'SET_ACTIVE_INTEGRATION'; payload: Integration | null }
  | { type: 'SET_SYNC_LOGS'; payload: SyncLog[] }
  | { type: 'ADD_SYNC_LOG'; payload: SyncLog }
  | { type: 'SET_HEALTH'; payload: IntegrationHealth };

/**
 * Integration Context Value
 */
interface IntegrationContextValue {
  // State
  integrations: Integration[];
  syncLogs: SyncLog[];
  activeIntegration: Integration | null;
  health: IntegrationHealth | null;
  isLoading: boolean;
  error: string | null;
  isSyncing: boolean;

  // Integration Actions
  loadIntegrations: (propertyId: string) => Promise<void>;
  createIntegration: (input: CreateIntegrationInput) => Promise<Integration>;
  updateIntegration: (
    id: string,
    updates: UpdateIntegrationInput
  ) => Promise<void>;
  deleteIntegration: (id: string) => Promise<void>;
  setActiveIntegration: (integration: Integration | null) => void;
  testConnection: (integrationId: string) => Promise<boolean>;

  // Sync Actions
  syncData: (integrationId: string, options: SyncOptions) => Promise<void>;
  loadSyncLogs: (integrationId: string, limit?: number) => Promise<void>;
  getLastSync: (integrationId: string, syncType: string) => SyncLog | undefined;

  // Health Actions
  checkHealth: (integrationId: string) => Promise<void>;
}

/**
 * Integration Reducer
 */
const integrationReducer = (
  state: IntegrationState,
  action: IntegrationAction
): IntegrationState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };

    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };

    case 'SET_SYNCING':
      return { ...state, isSyncing: action.payload };

    case 'SET_INTEGRATIONS':
      return { ...state, integrations: action.payload, isLoading: false };

    case 'ADD_INTEGRATION':
      return {
        ...state,
        integrations: [...state.integrations, action.payload],
        isLoading: false,
      };

    case 'UPDATE_INTEGRATION':
      return {
        ...state,
        integrations: state.integrations.map((integration) =>
          integration.id === action.payload.id
            ? {
                ...integration,
                ...action.payload.updates,
                updatedAt: new Date(),
              }
            : integration
        ),
        activeIntegration:
          state.activeIntegration?.id === action.payload.id
            ? {
                ...state.activeIntegration,
                ...action.payload.updates,
                updatedAt: new Date(),
              }
            : state.activeIntegration,
        isLoading: false,
      };

    case 'DELETE_INTEGRATION':
      return {
        ...state,
        integrations: state.integrations.filter(
          (integration) => integration.id !== action.payload
        ),
        activeIntegration:
          state.activeIntegration?.id === action.payload
            ? null
            : state.activeIntegration,
        isLoading: false,
      };

    case 'SET_ACTIVE_INTEGRATION':
      return { ...state, activeIntegration: action.payload };

    case 'SET_SYNC_LOGS':
      return { ...state, syncLogs: action.payload, isLoading: false };

    case 'ADD_SYNC_LOG':
      return {
        ...state,
        syncLogs: [action.payload, ...state.syncLogs],
        isLoading: false,
      };

    case 'SET_HEALTH':
      return { ...state, health: action.payload, isLoading: false };

    default:
      return state;
  }
};

/**
 * Initial State
 */
const initialState: IntegrationState = {
  integrations: [],
  syncLogs: [],
  activeIntegration: null,
  health: null,
  isLoading: false,
  error: null,
  isSyncing: false,
};

/**
 * Integration Context
 */
const IntegrationContext = createContext<IntegrationContextValue | undefined>(
  undefined
);

/**
 * Integration Provider Component
 */
export const IntegrationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(integrationReducer, initialState);

  /**
   * Load Integrations
   */
  const loadIntegrations = useCallback(async (propertyId: string) => {
    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      const { data, error } = await supabase
        .from('pms_integrations')
        .select('*')
        .eq('property_id', propertyId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const integrations: Integration[] = (data || []).map(
        (item: Record<string, unknown>) => ({
          id: item.id,
          propertyId: item.property_id,
          pmsType: item.pms_type,
          active: item.active,
          config: item.config || {},
          status: item.status,
          lastSync: item.last_sync ? new Date(item.last_sync) : undefined,
          createdAt: new Date(item.created_at),
          updatedAt: item.updated_at ? new Date(item.updated_at) : undefined,
        })
      );

      dispatch({ type: 'SET_INTEGRATIONS', payload: integrations });

      // Set first active integration as active
      const activeIntegration = integrations.find((i) => i.active);
      if (activeIntegration) {
        dispatch({
          type: 'SET_ACTIVE_INTEGRATION',
          payload: activeIntegration,
        });
      }
    } catch (error) {
      console.error('Error loading integrations:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load integrations' });
    }
  }, []);

  /**
   * Create Integration
   */
  const createIntegration = useCallback(
    async (input: CreateIntegrationInput): Promise<Integration> => {
      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        const { data, error } = await supabase
          .from('pms_integrations')
          .insert([
            {
              property_id: input.propertyId,
              pms_type: input.pmsType,
              config: input.config,
              active: true,
              status: 'disconnected',
              created_at: new Date().toISOString(),
            },
          ])
          .select()
          .single();

        if (error) throw await resolveFunctionsHttpError(error);

        const integration: Integration = {
          id: data.id,
          propertyId: data.property_id,
          pmsType: data.pms_type,
          active: data.active,
          config: data.config,
          status: data.status,
          createdAt: new Date(data.created_at),
        };

        dispatch({ type: 'ADD_INTEGRATION', payload: integration });
        return integration;
      } catch (error) {
        console.error('Error creating integration:', error);
        dispatch({
          type: 'SET_ERROR',
          payload: 'Failed to create integration',
        });
        throw error;
      }
    },
    []
  );

  /**
   * Update Integration
   */
  const updateIntegration = useCallback(
    async (id: string, updates: UpdateIntegrationInput) => {
      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        const { error } = await supabase
          .from('pms_integrations')
          .update({
            active: updates.active,
            config: updates.config,
            status: updates.status,
            updated_at: new Date().toISOString(),
          })
          .eq('id', id);

        if (error) throw await resolveFunctionsHttpError(error);

        dispatch({ type: 'UPDATE_INTEGRATION', payload: { id, updates } });
      } catch (error) {
        console.error('Error updating integration:', error);
        dispatch({
          type: 'SET_ERROR',
          payload: 'Failed to update integration',
        });
        throw error;
      }
    },
    []
  );

  /**
   * Delete Integration
   */
  const deleteIntegration = useCallback(async (id: string) => {
    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      const { error } = await supabase
        .from('pms_integrations')
        .delete()
        .eq('id', id);

      if (error) throw error;

      dispatch({ type: 'DELETE_INTEGRATION', payload: id });
    } catch (error) {
      console.error('Error deleting integration:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to delete integration' });
      throw error;
    }
  }, []);

  /**
   * Set Active Integration
   */
  const setActiveIntegration = useCallback(
    (integration: Integration | null) => {
      dispatch({ type: 'SET_ACTIVE_INTEGRATION', payload: integration });
    },
    []
  );

  /**
   * Test Connection
   */
  const testConnection = useCallback(
    async (integrationId: string): Promise<boolean> => {
      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        const integration = state.integrations.find(
          (i) => i.id === integrationId
        );
        if (!integration) {
          throw new Error('Integration not found');
        }

        // Call edge function to test connection
        const { data, error } = await supabase.functions.invoke(
          'test-pms-connection',
          {
            body: {
              integrationId: integration.id,
              pmsType: integration.pmsType,
              config: integration.config,
            },
          }
        );

        if (error) throw await resolveFunctionsHttpError(error);

        const isConnected = data?.success || false;

        // Update integration status
        await updateIntegration(integrationId, {
          status: isConnected ? 'connected' : 'error',
        });

        dispatch({ type: 'SET_LOADING', payload: false });
        return isConnected;
      } catch (error) {
        console.error('Error testing connection:', error);
        dispatch({ type: 'SET_ERROR', payload: 'Failed to test connection' });
        return false;
      }
    },
    [state.integrations, updateIntegration]
  );

  /**
   * Sync Data
   */
  const syncData = useCallback(
    async (integrationId: string, options: SyncOptions) => {
      dispatch({ type: 'SET_SYNCING', payload: true });

      try {
        const integration = state.integrations.find(
          (i) => i.id === integrationId
        );
        if (!integration) {
          throw new Error('Integration not found');
        }

        // Create sync log
        const syncLog: SyncLog = {
          id: Date.now().toString(),
          integrationId: integration.id,
          syncType: options.syncType,
          status: 'success',
          startTime: new Date(),
          recordsProcessed: 0,
          recordsFailed: 0,
        };

        dispatch({ type: 'ADD_SYNC_LOG', payload: syncLog });

        // Call edge function to perform sync
        const { data, error } = await supabase.functions.invoke(
          'sync-pms-data',
          {
            body: {
              integrationId: integration.id,
              pmsType: integration.pmsType,
              config: integration.config,
              options,
            },
          }
        );

        if (error) throw await resolveFunctionsHttpError(error);

        // Update sync log with results
        syncLog.endTime = new Date();
        syncLog.recordsProcessed = data?.recordsProcessed || 0;
        syncLog.recordsFailed = data?.recordsFailed || 0;
        syncLog.status = data?.status || 'success';
        syncLog.errors = data?.errors;

        // Update last sync time
        await updateIntegration(integrationId, {
          status: 'connected',
        });

        // Update last_sync in database
        await supabase
          .from('pms_integrations')
          .update({ last_sync: new Date().toISOString() })
          .eq('id', integrationId);

        dispatch({ type: 'SET_SYNCING', payload: false });
      } catch (error) {
        console.error('Error syncing data:', error);
        dispatch({ type: 'SET_ERROR', payload: 'Failed to sync data' });
        dispatch({ type: 'SET_SYNCING', payload: false });
        throw error;
      }
    },
    [state.integrations, updateIntegration]
  );

  /**
   * Load Sync Logs
   */
  const loadSyncLogs = useCallback(
    async (integrationId: string, limit: number = 50) => {
      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        const { data, error } = await supabase
          .from('sync_logs')
          .select('*')
          .eq('integration_id', integrationId)
          .order('start_time', { ascending: false })
          .limit(limit);

        if (error) throw await resolveFunctionsHttpError(error);

        const syncLogs: SyncLog[] = (data || []).map(
          (item: Record<string, unknown>) => ({
            id: item.id,
            integrationId: item.integration_id,
            syncType: item.sync_type,
            status: item.status,
            startTime: new Date(item.start_time),
            endTime: item.end_time ? new Date(item.end_time) : undefined,
            recordsProcessed: item.records_processed,
            recordsFailed: item.records_failed,
            errors: item.errors,
          })
        );

        dispatch({ type: 'SET_SYNC_LOGS', payload: syncLogs });
      } catch (error) {
        console.error('Error loading sync logs:', error);
        dispatch({ type: 'SET_ERROR', payload: 'Failed to load sync logs' });
      }
    },
    []
  );

  /**
   * Get Last Sync
   */
  const getLastSync = useCallback(
    (integrationId: string, syncType: string): SyncLog | undefined => {
      return state.syncLogs
        .filter(
          (log) =>
            log.integrationId === integrationId && log.syncType === syncType
        )
        .sort((a, b) => b.startTime.getTime() - a.startTime.getTime())[0];
    },
    [state.syncLogs]
  );

  /**
   * Check Health
   */
  const checkHealth = useCallback(
    async (integrationId: string) => {
      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        const integration = state.integrations.find(
          (i) => i.id === integrationId
        );
        if (!integration) {
          throw new Error('Integration not found');
        }

        const lastSuccessfulSync = state.syncLogs
          .filter(
            (log) =>
              log.integrationId === integrationId && log.status === 'success'
          )
          .sort((a, b) => b.startTime.getTime() - a.startTime.getTime())[0];

        const lastError = state.syncLogs
          .filter(
            (log) =>
              log.integrationId === integrationId && log.status === 'failed'
          )
          .sort((a, b) => b.startTime.getTime() - a.startTime.getTime())[0];

        const health: IntegrationHealth = {
          status:
            integration.status === 'connected'
              ? 'healthy'
              : integration.status === 'error'
                ? 'down'
                : 'degraded',
          lastSuccessfulSync: lastSuccessfulSync?.startTime,
          lastError: lastError?.errors?.[0]?.message,
          responseTime: undefined,
          uptime: undefined,
        };

        dispatch({ type: 'SET_HEALTH', payload: health });
      } catch (error) {
        console.error('Error checking health:', error);
        dispatch({ type: 'SET_ERROR', payload: 'Failed to check health' });
      }
    },
    [state.integrations, state.syncLogs]
  );

  const value: IntegrationContextValue = {
    // State
    integrations: state.integrations,
    syncLogs: state.syncLogs,
    activeIntegration: state.activeIntegration,
    health: state.health,
    isLoading: state.isLoading,
    error: state.error,
    isSyncing: state.isSyncing,

    // Actions
    loadIntegrations,
    createIntegration,
    updateIntegration,
    deleteIntegration,
    setActiveIntegration,
    testConnection,
    syncData,
    loadSyncLogs,
    getLastSync,
    checkHealth,
  };

  return (
    <IntegrationContext.Provider value={value}>
      {children}
    </IntegrationContext.Provider>
  );
};

/**
 * useIntegration Hook
 * Custom hook to access Integration context
 */
export const useIntegration = (): IntegrationContextValue => {
  const context = useContext(IntegrationContext);

  if (context === undefined) {
    throw new Error(
      'useIntegration must be used within an IntegrationProvider'
    );
  }

  return context;
};

export default IntegrationContext;
